import dayjs from 'dayjs';
import { useFormik } from 'formik';
import isNil from 'lodash/isNil';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { number, object, string } from 'yup';

import { ApiDexBuySellBot } from 'api';
import { DexBuySellBotContext } from 'context/DexBuySellBotContext';
import { useTypedDispatch, useTypedSelector } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { bn } from 'tools/math';
import {
  EnumDexBuySellOpts,
  EnumDexBuySellType,
  IBuySellBotTaskItemInner,
  IDexBuySellBotDirection,
  IDexBuySellBotOptsType1wNs,
  IDexBuySellBotTypeUnit,
} from 'types/bots';
import { IDexPair } from 'types/pairs';
import { dexTokenToBN } from 'utils';
import { convertTokenDEX } from 'utils/convertToken';
import { humanizeDexToken } from 'utils/convertToken';
import { mapDexBuySellTaskType } from 'utils/dex/mapDexBuySellTaskType';
import { durationToMs } from 'utils/duration';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

type TaskModalForm = {
  type: IDexBuySellBotTypeUnit;
  startDate: number | null;
  totalTokenAmount: string;
  minAmount: string;
  maxAmount: string;
  minDelay: number | null;
  maxDelay: number | null;
  advanceCoefficient: string;
  priceThresholdMin: string;
  priceThresholdMax: string;
  amountDisperseCoeffMin: string;
  amountDisperseCoeffMax: string;
  skipTransactionsMin: string;
  skipTransactionsMax: string;
};

type TaskModalProps = {
  direction: Capitalize<IDexBuySellBotDirection>;
  task?: IBuySellBotTaskItemInner;
  mode: 'create' | 'edit';
  onClose: VoidFunction;
};

const getConvertToken = (curr: string, dexPair: IDexPair) => {
  const convertTokenAddress = curr.toLowerCase();

  const convertToken =
    convertTokenAddress === dexPair.token_base.address.toLowerCase()
      ? dexPair.token_base
      : convertTokenAddress === dexPair.token_fee.address.toLowerCase()
        ? dexPair.token_fee
        : { symbol: 'USD' };

  return convertToken;
};

export const useTaskModal = ({ direction, task, mode, onClose }: TaskModalProps) => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const dispatch = useTypedDispatch();
  const { handleLoadRecords, handleLoadBotSettings } = useContext(DexBuySellBotContext);
  const [loading, setLoading] = useState(false);
  const [edited, setEdited] = useState(false);
  const [totalCurrency, setTotalCurrency] = useState<string>(dexPair.token_base.symbol);
  const [tradesCurrency, setTradesCurrency] = useState<string>(dexPair.token_base.symbol);
  const [thresholdCurrency, setThresholdCurrency] = useState<'quote' | 'usd'>('quote');

  const initialValues = useMemo(() => {
    if (task) {
      const taskOptsKey = mapDexBuySellTaskType(task.task.type);

      const options = task.task[taskOptsKey] as IDexBuySellBotOptsType1wNs;

      const minDelayMs = durationToMs(options.min_pause);
      const maxDelayMs = durationToMs(options.max_pause);

      const commonParams = {
        type: task.task.type,
        startDate: dayjs(options.start_time).valueOf(),
        minAmount: humanizeDexToken({
          amount: options.trade_amount_range.min_value,
          dexPair: dexPair,
          token: options.trade_amount_range.currency,
        }),
        maxAmount: humanizeDexToken({
          amount: options.trade_amount_range.max_value,
          dexPair: dexPair,
          token: options.trade_amount_range.currency,
        }),
        minDelay: minDelayMs,
        maxDelay: maxDelayMs,
        advanceCoefficient: (Number(options.advance_coefficient) * 100).toString(),
        priceThresholdMin: options.price_threshold.min,
        priceThresholdMax: options.price_threshold.max,
      };

      if (taskOptsKey === EnumDexBuySellOpts.OPT_TYPE_1W_NS) {
        return {
          ...commonParams,
          totalTokenAmount: humanizeDexToken({
            amount: options.total_token_amount.value,
            dexPair: dexPair,
            token: options.total_token_amount.currency,
          }),
          amountDisperseCoeffMin: (options.amount_disperse_coeff_min * 100).toString(),
          amountDisperseCoeffMax: (options.amount_disperse_coeff_max * 100).toString(),
          skipTransactionsMin: options.skip_transactions_min.toString(),
          skipTransactionsMax: options.skip_transactions_max.toString(),
        };
      }

      if (taskOptsKey === EnumDexBuySellOpts.OPT_TYPE_1W_1S) {
        return {
          ...commonParams,
        };
      }
    }

    return {
      type: EnumDexBuySellType.TYPE_1W_NS,
      startDate: dayjs().second(0).millisecond(0).valueOf(),
      totalTokenAmount: '',
      minAmount: '',
      maxAmount: '',
      minDelay: 3000,
      maxDelay: 1000 * 60 * 15,
      advanceCoefficient: '30',
      priceThresholdMin: '',
      priceThresholdMax: '',
      amountDisperseCoeffMin: '50',
      amountDisperseCoeffMax: '80',
      skipTransactionsMin: '2',
      skipTransactionsMax: '5',
    };
  }, [task, dexPair]);

  useEffect(() => {
    if (task) {
      const taskOptsKey = mapDexBuySellTaskType(task.task.type);

      const options = task.task[taskOptsKey] as IDexBuySellBotOptsType1wNs;

      const tradesToken = getConvertToken(options.trade_amount_range.currency, dexPair);
      const totalToken = options.total_token_amount
        ? getConvertToken(options.total_token_amount.currency, dexPair)
        : undefined;

      setTradesCurrency(tradesToken.symbol);
      if (totalToken) {
        setTotalCurrency(totalToken.symbol);
      }
      setThresholdCurrency(
        options.price_threshold.currency_quote === '0x0000000000000000000000000000000000000001'
          ? 'usd'
          : 'quote',
      );
    }
  }, [task, dexPair]);

  const [formError, setFormError] = useState<string | undefined>(undefined);

  const validationSchema = useMemo(
    () =>
      object({
        startDate: number().test(
          'startDate',
          '"Start time" field is required',
          startDate => !isNil(startDate) && startDate > 0,
        ),
        totalTokenAmount: number().when('type', (types, schema) =>
          types[0] === EnumDexBuySellType.TYPE_1W_NS
            ? mode === 'create'
              ? schema
                  .required('"Total amount" field is required')
                  .positive('"Total amount" must be positive')
              : schema.required('"Total amount" field is required')
            : schema.optional(),
        ),
        minAmount: number()
          .test(
            'minAmount',
            '"Min amount" field is required',
            minAmount => !isNaN(Number(minAmount)) && Number(minAmount) > 0,
          )
          .test(
            'minAmount',
            '"Min amount" must be less than "Total amount"',
            (minAmount, testContext) => {
              if (
                !minAmount ||
                isNaN(minAmount) ||
                isNaN(Number(testContext.parent.totalTokenAmount))
              )
                return true;

              return convertTokenDEX({
                from: tradesCurrency,
                to: dexPair.token_base.symbol,
                amount: minAmount.toString(),
                pair: dexPair,
                decimals: dexPair.token_base.decimals,
              }).lte(
                convertTokenDEX({
                  from: totalCurrency,
                  to: dexPair.token_base.symbol,
                  amount: testContext.parent.totalTokenAmount,
                  pair: dexPair,
                  decimals: dexPair.token_base.decimals,
                }),
              );
            },
          ),
        maxAmount: number()
          .test(
            'maxAmount',
            '"Max amount" field is required',
            maxAmount => !isNaN(Number(maxAmount)) && Number(maxAmount) > 0,
          )
          .test('maxAmount', 'Enter valid "Max amount"', (maxAmount, testContext) => {
            if (
              !maxAmount ||
              !testContext.parent.minAmount ||
              isNaN(maxAmount) ||
              isNaN(Number(testContext.parent.totalTokenAmount)) ||
              isNaN(testContext.parent.minAmount)
            )
              return true;
            const converted = convertTokenDEX({
              from: tradesCurrency,
              to: dexPair.token_base.symbol,
              amount: maxAmount.toString(),
              pair: dexPair,
              decimals: dexPair.token_base.decimals,
            });

            return (
              converted.lte(
                convertTokenDEX({
                  from: totalCurrency,
                  to: dexPair.token_base.symbol,
                  amount: testContext.parent.totalTokenAmount,
                  pair: dexPair,
                  decimals: dexPair.token_base.decimals,
                }),
              ) &&
              converted.gte(
                convertTokenDEX({
                  from: tradesCurrency,
                  to: dexPair.token_base.symbol,
                  amount: testContext.parent.minAmount,
                  pair: dexPair,
                  decimals: dexPair.token_base.decimals,
                }),
              )
            );
          }),
        minDelay: number()
          .nullable()
          .test(
            'minDelay',
            '"Min delay" field is required',
            (minDelay: number | undefined | null) => !isNil(minDelay),
          ),
        maxDelay: number()
          .nullable()
          .test(
            'maxDelay',
            '"Max delay" field is required',
            (maxDelay: number | undefined | null) => !isNil(maxDelay),
          ),
        priceThresholdMin: string()
          .required('"Min threshold" field is required')
          .test(
            'priceThresholdMin',
            '"Min threshold" field is required',
            (value: string | undefined) => !isNil(Number(value)) && bn(value).gte(bn('0')),
          ),
        priceThresholdMax: string()
          .required('"Max threshold" field is required')
          .test(
            'priceThresholdMax',
            '"Max threshold" field must be greater than "Min threshold"',
            function (priceThresholdMax: string | undefined, testContext) {
              const priceThresholdMin = testContext.parent.priceThresholdMin;

              return bn(priceThresholdMin).lte(bn(priceThresholdMax));
            },
          ),
      }),
    [mode, dexPair, totalCurrency, tradesCurrency],
  );

  const handleAddTask = async ({
    type,
    startDate,
    totalTokenAmount,
    minAmount,
    maxAmount,
    minDelay,
    maxDelay,
    priceThresholdMin,
    priceThresholdMax,
  }: TaskModalForm) => {
    if (!startDate) return;

    setLoading(true);

    const _priceThresholdMin =
      priceThresholdMin !== '' && !isNaN(Number(priceThresholdMin))
        ? normalizeNumber(priceThresholdMin)
        : '0';

    const _priceThresholdMax =
      priceThresholdMax !== '' && !isNaN(Number(priceThresholdMax))
        ? normalizeNumber(priceThresholdMax)
        : '0';

    const tradesConvertedToken =
      tradesCurrency === dexPair.token_base.symbol
        ? dexPair.token_base.address
        : tradesCurrency === dexPair.token_fee.symbol
          ? dexPair.token_fee.address
          : '0x0000000000000000000000000000000000000001';

    const totalConvertedToken =
      totalCurrency === dexPair.token_base.symbol
        ? dexPair.token_base.address
        : totalCurrency === dexPair.token_fee.symbol
          ? dexPair.token_fee.address
          : '0x0000000000000000000000000000000000000001';

    const commonParams = {
      enabled: task ? task.task[mapDexBuySellTaskType(task.task.type)].enabled : false,
      direction: direction.toLowerCase() as IDexBuySellBotDirection,
      start_time: dayjs(startDate).toISOString(),
      trade_amount_range: {
        min_value: dexTokenToBN({ amount: minAmount, token: tradesCurrency, dexPair }).toString(),
        max_value: dexTokenToBN({ amount: maxAmount, token: tradesCurrency, dexPair }).toString(),
        currency: tradesConvertedToken,
      },
      min_pause: `${minDelay}ms`,
      max_pause: `${maxDelay}ms`,
      advance_coefficient: 0,
      price_threshold: {
        max: _priceThresholdMax,
        min: _priceThresholdMin,
        currency_base: dexPair.token_base.address,
        currency_quote:
          thresholdCurrency === 'usd'
            ? '0x0000000000000000000000000000000000000001'
            : dexPair.token_quote.address,
      },
    };

    const updatedTask =
      type === EnumDexBuySellType.TYPE_1W_NS
        ? {
            ...commonParams,
            total_token_amount: {
              value: dexTokenToBN({
                amount: totalTokenAmount,
                token: totalCurrency,
                dexPair,
              }).toString(),
              currency: totalConvertedToken,
            },
            use_mempool: 'off',
            amount_disperse_coeff_min: 1,
            amount_disperse_coeff_max: 1,
            skip_transactions_min: 0,
            skip_transactions_max: 0,
          }
        : commonParams;

    try {
      const taskId = task?.task.id;

      let _isSuccess = false;
      let _errorMessage: string | null = null;

      if (mode === 'edit' && taskId) {
        const { isSuccess, errorMessage } = await ApiDexBuySellBot.updateBuySellTask({
          options: updatedTask,
          taskId,
          type,
        });

        _isSuccess = isSuccess;
        _errorMessage = errorMessage;
      }

      if (mode === 'create') {
        const { isSuccess, errorMessage } = await ApiDexBuySellBot.createBuySellTask({
          options: updatedTask,
          pairId: dexPair.id,
          type,
        });

        _isSuccess = isSuccess;
        _errorMessage = errorMessage;
      }

      if (!_isSuccess) {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: _errorMessage ?? 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      } else {
        handleLoadRecords();
        handleLoadBotSettings();
        onClose();

        dispatch(
          setAlertState({
            type: 'success',
            text:
              mode === 'create'
                ? 'You successfully create new task!'
                : 'You successfully edit task!',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      }
    } catch (e) {
      console.error(e);
      dispatch(
        setAlertState({
          type: 'failed-img',
          text: 'Something went wrong',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
          },
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const { handleSubmit, setFieldValue, setFieldTouched, values, errors, touched, resetForm } =
    useFormik<TaskModalForm>({
      initialValues: initialValues as TaskModalForm,
      onSubmit: handleAddTask,
      validationSchema,
    });

  const handleSetFieldValue = useCallback(
    (field: string, value: any) => {
      setEdited(true);

      setFieldValue(field, value);
    },
    [setFieldValue],
  );

  useEffect(() => {
    const handleSetFormError = () => {
      const keys = Object.keys(errors);

      for (const key of keys) {
        const keyWithType = key as keyof typeof errors;

        if (errors[keyWithType] && touched[keyWithType]) {
          setFormError(errors[keyWithType]);
          return;
        }
      }

      setFormError(undefined);
    };

    handleSetFormError();
  }, [errors, touched]);

  return {
    validationSchema,
    loading,
    initialValues,
    values,
    handleSubmit,
    setFieldValue: handleSetFieldValue,
    setFieldTouched,
    formError,
    thresholdCurrency,
    setThresholdCurrency,
    totalCurrency,
    setTotalCurrency,
    tradesCurrency,
    setTradesCurrency,
    resetForm,
    edited,
    errors,
    touched,
  };
};
